/**
 * Kategorien und Standardordner (Dokumentenklassen) der Akte – ausgelagert
 * aus SectionDokumente.vue (Kundenfeedback Kanzlei Polz 18.10.2026, Nr. V).
 *
 * Spiegel von backend/internal/fall/form/doc_class_catalog.go. Die Regeln
 * (Standard / bedingt / Vorlage / Bestand) stehen in docClassCatalog.ts.
 */
import {
  ClipboardCheck, Camera, Car, FileSignature, Files, Gavel, HeartPulse, Landmark, Layers, Mail,
  Receipt, ReceiptText, ShieldCheck, Siren, Truck, Wrench, Scale,
} from 'lucide-vue-next'
import { classDescription, classMode, classScope, isAutoClass } from './docClassCatalog'

export const CATEGORIES = [
  { value: 'originaldokument', label: 'Originaldokument' },
  { value: 'polizeibericht', label: 'Polizeibericht' },
  { value: 'staatsanwaltschaft', label: 'Staatsanwaltschaft' },
  { value: 'versicherungsschrift', label: 'Versicherungsschreiben' },
  // KARS Sync 16.10.2026 (Ordnertabelle Kanzlei Polz): Unterordner der
  // Versicherungen – Gegnerische Haftpflicht, Vollkasko Kunde,
  // Regulierungsbeauftragter, GAP, Auslandsschadenschutz.
  { value: 'versicherung_haftpflicht', label: 'Gegnerische Haftpflicht' },
  { value: 'versicherung_kasko', label: 'Vollkasko Kunde' },
  { value: 'versicherung_regulierungsbeauftragter', label: 'Regulierungsbeauftragter' },
  { value: 'versicherung_gap', label: 'GAP' },
  { value: 'versicherung_auslandsschadenschutz', label: 'Auslandsschadenschutz' },
  { value: 'versicherung_rechtsschutz', label: 'Rechtsschutz' },
  { value: 'versicherung_schutzbrief', label: 'Schutzbrief' },
  { value: 'versicherung_sonstige', label: 'Sonstige Versicherung' },
  { value: 'anwaltsschreiben', label: 'Anwaltsschreiben' },
  { value: 'gerichtsdokument', label: 'Gerichtsdokument' },
  // KARS Sync 16.10.2026: Unterordner „Gericht' – Instanzen, Schriftsätze,
  // Verfügungen/Termine/Hinweise.
  { value: 'gericht_instanz1', label: 'I. Instanz' },
  { value: 'gericht_instanz2', label: 'II. Instanz' },
  { value: 'schriftsatz_eigen', label: 'Schriftsätze wir' },
  { value: 'schriftsatz_gegner', label: 'Schriftsätze Gegner' },
  { value: 'schriftsatz_streitverkuendeter', label: 'Schriftsätze Streitverkündeter' },
  { value: 'gericht_verfuegung', label: 'Verfügungen, Termine, Hinweise' },
  // KARS Sync 16.10.2026: eigene Ordner für Mietwagen und Abschlepper.
  { value: 'mietvertrag', label: 'Mietvertrag' },
  { value: 'abschleppprotokoll', label: 'Abschleppprotokoll' },
  { value: 'gutachten', label: 'Gutachten' },
  { value: 'dat_kalkulation', label: 'DAT-Kalkulation' },
  { value: 'kostenvoranschlag', label: 'Kostenvoranschlag' },
  { value: 'rechnung', label: 'Rechnung' },
  { value: 'aerztlicherbericht', label: 'Ärztlicher Bericht' },
  { value: 'vollmacht', label: 'Vollmacht' },
  // Kundenfeedback 25.10.2026 (IX): in KARS erstellte Vollmacht zum Versand per
  // Post – liegt im Ordner „Kunde', zählt aber nicht als eingegangene Vollmacht.
  { value: 'vollmacht-ausdruck', label: 'Vollmacht zum Versand (noch nicht unterschrieben)' },
  { value: 'zeugenfragebogen', label: 'Zeugenfragebogen' },
  { value: 'reparatur', label: 'Reparatur' },
  // Kundenfeedback 14.10.2026 (IX): internes Werkstatt-Dokument; das
  // Bestelldatum wird als „Ersatzteilbestellung' in den Ablaufplan übernommen.
  { value: 'materialbestellschein', label: 'Materialbestellschein' },
  { value: 'pruefbericht', label: 'Prüfbericht' },
  { value: 'gegenueberstellung', label: 'Gegenüberstellung' },
  { value: 'fragebogen-version', label: 'Fragebogen-Version' },
  // Kundenfeedback 26.10.2026 (X): PDF-Protokoll einer Abgleich-Entscheidung.
  { value: 'abgleich-fragebogen', label: 'Abgleich Fragebogen (Entscheidung)' },
  { value: 'abgleich-gutachten', label: 'Abgleich Gutachten (Entscheidung)' },
  { value: 'foto', label: 'Foto / Beweis' },
  { value: 'vertrag', label: 'Vertrag' },
  { value: 'korrespondenz', label: 'Korrespondenz' },
  // Kasko-Fallansicht (Spec 2026-09-19, §9) — Spiegel von form.ValidCategories
  { value: 'abtretung', label: 'Abtretungserklärung / RKÜ' },
  { value: 'uebernahmebestaetigung', label: 'Übernahmebestätigung' },
  { value: 'abrechnungsschreiben', label: 'Abrechnungsschreiben Versicherung' },
  { value: 'nachforderung', label: 'Nachforderungsschreiben' },
  // Kundenfeedback 16.10.2026 (Kanzlei Polz): Quittungen/Belege zu
  // Schadenpositionen bekommen einen eigenen Ordner „Schadenbelege'.
  { value: 'schadenbeleg', label: 'Schadenbeleg' },
  // Kundenfeedback 16.10.2026 Teil 2 (Nr. II, Entscheidung a): „Bank / Leasing'
  // und Schadenfotos des Fahrzeugs getrennt von den Beweismittel-Fotos.
  { value: 'bank_leasing', label: 'Bank / Leasing' },
  { value: 'schadenfoto', label: 'Schadenfoto / Fahrzeugfoto' },
  { value: 'sonstiges', label: 'Sonstiges' },
]

export interface DocClassDef {
  id: string
  label: string
  icon: unknown
  color: string
  side: 'left' | 'right'
  categories: string[]
  defaultCategory: string
  /** Sammelansicht: zeigt zusätzlich Dokumente dieser Kategorien anderer Ordner. */
  aggregateCategories?: string[]
  /** Unterordner eines Standardordners (Klassen-ID des Elternordners). */
  parentId?: string | null
}

// Standardordner nach der Ordnertabelle der Kanzlei Polz (18.10.2026, Nr. V):
// Standard (automatisch): Kunde, Gutachter / KVA, Unterlagen Fahrzeugschaden
// gesamt, Schadenbelege mit Unterordner Rechnungen, Versicherungen mit
// Unterordnern, Sonstiges. Bedingt: Bank / Leasing, Autohaus / Werkstatt.
// Nur Vorlage: Mietwagen, Abschlepper, Beweismittel, Polizei und
// Staatsanwaltschaft, Personenschaden, Gericht mit Unterordnern.
// „E-Mails & Korrespondenz" entfällt (Nr. IV) – nur Bestandsakten mit
// Dokumenten dieser Kategorie zeigen ihn noch.
//
// Icons und Farben sind in der Oberfläche entfallen (Kundenwunsch „nur der
// Name"); `icon`/`color` bleiben für die Chronologie erhalten. `side`
// bestimmt die Schiene der Kachelansicht (Unterordner erscheinen dort im
// Elternordner).
const RAW_CLASSES: DocClassDef[] = [
  // ── Linke Schiene ──
  { id: 'vollmacht', label: 'Kunde', icon: FileSignature, color: '#7c3aed', side: 'left', categories: ['vollmacht', 'vollmacht-ausdruck', 'vertrag', 'abtretung', 'fragebogen-version', 'abgleich-fragebogen'], defaultCategory: 'vollmacht' },
  { id: 'gutachten', label: 'Gutachter / KVA', icon: ClipboardCheck, color: '#0891b2', side: 'left', categories: ['gutachten', 'dat_kalkulation', 'kostenvoranschlag', 'gegenueberstellung', 'abgleich-gutachten'], defaultCategory: 'gutachten' },
  { id: 'fahrzeugschaden', label: 'Unterlagen Fahrzeugschaden gesamt', icon: Layers, color: '#0f766e', side: 'left', categories: ['schadenfoto'], aggregateCategories: ['rechnung', 'gutachten', 'kostenvoranschlag', 'dat_kalkulation', 'pruefbericht'], defaultCategory: 'schadenfoto' },
  { id: 'polizei', label: 'Polizei und Staatsanwaltschaft', icon: Siren, color: '#dc2626', side: 'left', categories: ['polizeibericht', 'staatsanwaltschaft'], defaultCategory: 'polizeibericht' },
  // „Versicherungen" mit Unterordnern; der Prüfbericht bleibt im Elternordner.
  { id: 'versicherung', label: 'Versicherungen', icon: ShieldCheck, color: '#2563eb', side: 'left', categories: ['versicherungsschrift', 'versicherung_rechtsschutz', 'versicherung_schutzbrief', 'versicherung_sonstige', 'uebernahmebestaetigung', 'abrechnungsschreiben', 'nachforderung', 'pruefbericht'], defaultCategory: 'versicherungsschrift' },
  { id: 'versicherung_haftpflicht', label: 'Gegnerische Haftpflicht', icon: ShieldCheck, color: '#2563eb', side: 'left', parentId: 'versicherung', categories: ['versicherung_haftpflicht'], defaultCategory: 'versicherung_haftpflicht' },
  { id: 'versicherung_kasko', label: 'Vollkasko Kunde', icon: ShieldCheck, color: '#2563eb', side: 'left', parentId: 'versicherung', categories: ['versicherung_kasko'], defaultCategory: 'versicherung_kasko' },
  { id: 'versicherung_regulierungsbeauftragter', label: 'Regulierungsbeauftragter', icon: ShieldCheck, color: '#2563eb', side: 'left', parentId: 'versicherung', categories: ['versicherung_regulierungsbeauftragter'], defaultCategory: 'versicherung_regulierungsbeauftragter' },
  { id: 'versicherung_gap', label: 'GAP', icon: ShieldCheck, color: '#2563eb', side: 'left', parentId: 'versicherung', categories: ['versicherung_gap'], defaultCategory: 'versicherung_gap' },
  { id: 'versicherung_auslandsschadenschutz', label: 'Auslandsschadenschutz', icon: ShieldCheck, color: '#2563eb', side: 'left', parentId: 'versicherung', categories: ['versicherung_auslandsschadenschutz'], defaultCategory: 'versicherung_auslandsschadenschutz' },
  // „Gericht" mit Unterordnern (nur Vorlage).
  { id: 'recht', label: 'Gericht', icon: Gavel, color: '#1d4ed8', side: 'left', categories: ['anwaltsschreiben', 'gerichtsdokument'], defaultCategory: 'anwaltsschreiben' },
  { id: 'gericht_instanz1', label: 'I. Instanz', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['gericht_instanz1'], defaultCategory: 'gericht_instanz1' },
  { id: 'gericht_instanz2', label: 'II. Instanz', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['gericht_instanz2'], defaultCategory: 'gericht_instanz2' },
  { id: 'schriftsatz_eigen', label: 'Schriftsätze wir', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['schriftsatz_eigen'], defaultCategory: 'schriftsatz_eigen' },
  { id: 'schriftsatz_gegner', label: 'Schriftsätze Gegner', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['schriftsatz_gegner'], defaultCategory: 'schriftsatz_gegner' },
  { id: 'schriftsatz_streitverkuendeter', label: 'Schriftsätze Streitverkündeter', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['schriftsatz_streitverkuendeter'], defaultCategory: 'schriftsatz_streitverkuendeter' },
  { id: 'gericht_verfuegung', label: 'Verfügungen, Termine, Hinweise, sonstiges', icon: Scale, color: '#1d4ed8', side: 'left', parentId: 'recht', categories: ['gericht_verfuegung'], defaultCategory: 'gericht_verfuegung' },
  { id: 'beweise', label: 'Beweismittel', icon: Camera, color: '#ea580c', side: 'left', categories: ['foto', 'zeugenfragebogen'], defaultCategory: 'foto' },
  // ── Rechte Schiene ──
  // Quittungen/Belege zu Schadenpositionen mit Unterordner „Rechnungen".
  { id: 'schadenbelege', label: 'Schadenbelege', icon: ReceiptText, color: '#65a30d', side: 'right', categories: ['schadenbeleg'], defaultCategory: 'schadenbeleg' },
  // „Rechnungen": Ablage für Rechnungen ohne erkannte Art + Sammelansicht aller Rechnungen.
  { id: 'rechnungen', label: 'Rechnungen', icon: Receipt, color: '#16a34a', side: 'right', parentId: 'schadenbelege', categories: ['rechnung'], aggregateCategories: ['rechnung'], defaultCategory: 'rechnung' },
  { id: 'reparatur', label: 'Autohaus / Werkstatt', icon: Wrench, color: '#0076e5', side: 'right', categories: ['reparatur', 'materialbestellschein'], defaultCategory: 'reparatur' },
  { id: 'mietwagen', label: 'Mietwagen', icon: Car, color: '#4f46e5', side: 'right', categories: ['mietvertrag'], defaultCategory: 'mietvertrag' },
  { id: 'abschlepper', label: 'Abschlepper', icon: Truck, color: '#a16207', side: 'right', categories: ['abschleppprotokoll'], defaultCategory: 'abschleppprotokoll' },
  { id: 'medizin', label: 'Personenschaden', icon: HeartPulse, color: '#db2777', side: 'right', categories: ['aerztlicherbericht'], defaultCategory: 'aerztlicherbericht' },
  // Nur noch Bestand (Nr. IV): E-Mail-Korrespondenz gehört in die Korrespondenz.
  { id: 'korrespondenz', label: 'E-Mails & Korrespondenz', icon: Mail, color: '#9a4d00', side: 'right', categories: ['korrespondenz'], defaultCategory: 'korrespondenz' },
  { id: 'bank_leasing', label: 'Bank / Leasing', icon: Landmark, color: '#0f766e', side: 'right', categories: ['bank_leasing'], defaultCategory: 'bank_leasing' },
  { id: 'sonstiges', label: 'Sonstiges', icon: Files, color: '#64748b', side: 'right', categories: ['originaldokument', 'sonstiges'], defaultCategory: 'sonstiges' },
]

const LABEL_BY_ID = new Map(RAW_CLASSES.map(c => [c.id, c.label]))

export const DOC_CLASSES = RAW_CLASSES.map(cls => ({
  ...cls,
  parentId: cls.parentId ?? null,
  // Pfad für Auswahllisten („Versicherungen / GAP").
  pathLabel: cls.parentId ? `${LABEL_BY_ID.get(cls.parentId)} / ${cls.label}` : cls.label,
  description: classDescription(cls.id),
  auto: isAutoClass(cls.id),
  mode: classMode(cls.id),
  scope: classScope(cls.id),
}))

/** Rechnungsart → Ordner; ohne erkannte Art bleibt die Rechnung in „Rechnungen". */
export const INVOICE_KIND_TO_CLASS: Readonly<Record<string, string>> = {
  'gutachter-rechnung': 'gutachten',
  'reparatur-rechnung': 'reparatur',
  'mietwagen-rechnung': 'mietwagen',
}

/** Frühere Klassen-IDs (Deep-Links ?openClass=…, z. B. aus „Beteiligte"). */
export const LEGACY_CLASS_IDS: Readonly<Record<string, string>> = { zeugen: 'beweise', fotos: 'beweise', fragebogen: 'vollmacht' }

/** Schnelles category → classId Lookup (unbekannte Kategorien → „sonstiges"). */
export const CATEGORY_TO_CLASS: Readonly<Record<string, string>> = Object.fromEntries(
  DOC_CLASSES.flatMap(cls => cls.categories.map(cat => [cat, cls.id])),
)

/** Unterordner eines Standardordners (Klassen-IDs). */
export function childClassIds (parentId: string): string[] {
  return DOC_CLASSES.filter(c => c.parentId === parentId).map(c => c.id)
}
